import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaComments,
  FaExternalLinkAlt,
  FaPaperPlane,
  FaRedo,
  FaRobot,
  FaTimes,
} from "react-icons/fa";
import "./Chatbot.css";

const CONTACT_DETAILS = {
  address:
    "Western House, 17th Floor, Wing C, 8-10 Broad St, Lagos Island, Lagos",
  email: "infoinsurance@thrivenig.com",
  deskOne: "+234 818 099 6418",
  deskTwo: "+234 708 794 2708",
  whatsapp: "https://wa.me/2348180996418",
};

const PRODUCTS = [
  {
    id: "life",
    title: "Life and Personal Insurance",
    category: "Personal",
    route: "/products/life&personal-insurance",
    summary:
      "Protection for people, families, and businesses that need financial continuity when a key person or loved one dies.",
    covers: [
      "Keyman Insurance for business profit loss, continuity, and replacement costs after the death of a key person",
      "Term Life cover for a selected amount and period, with benefits paid to beneficiaries if death occurs within the term",
    ],
    bestFor: ["families", "business owners", "employers", "key employees"],
    keywords: [
      "life",
      "personal",
      "family",
      "death",
      "beneficiary",
      "beneficiaries",
      "term life",
      "keyman",
      "key man",
      "key person",
      "loved ones",
      "health",
    ],
  }, 
  {
    id: "motor",
    title: "Motor Insurance",
    category: "Personal",
    route: "/products/motor-insurance",
    summary:
      "Cover for vehicles and road-related risks, including damage, theft, third-party liabilities, goods in transit, money, and all-risk property needs.",
    covers: [
      "Comprehensive Motor Insurance for accidental vehicle damage and third-party risks",
      "Third Party Fire and Theft for fire, theft, and third-party claims",
      "Third Party cover for basic liability",
      "Goods in Transit for items moved by road or rail",
      "Money Insurance for cash in transit or storage",
      "All Risk Insurance for theft, loss, or accidental damage to property",
    ],
    bestFor: [
      "vehicle owners",
      "drivers",
      "fleet managers",
      "transport businesses",
    ],
    keywords: [
      "motor",
      "car",
      "vehicle",
      "auto",
      "driver",
      "fleet",
      "third party",
      "fire and theft",
      "comprehensive",
      "goods in transit",
      "git",
      "road",
      "rail",
      "cash",
      "money",
      "all risk",
      "accident",
    ],
  },
  {
    id: "property",
    title: "Property Insurance",
    category: "Business",
    route: "/products/property-insurance",
    summary:
      "Protection for buildings, contents, stock, homes, rent, and business premises against insured physical damage and interruption risks.",
    covers: [
      "Fire and Special Perils for fire, lightning, explosion, storm, flood, earthquake, buildings, contents, and rent",
      "Burglary Insurance for theft involving forceful entry, including contents and stock",
      "Household Insurance for homes, fixtures, valuables, public liability, fire, burglary, and accidental damage",
      "Industrial All Risk for combined fire, burglary, fidelity, money, and business interruption protection",
    ],
    bestFor: [
      "homeowners",
      "landlords",
      "retailers",
      "manufacturers",
      "asset owners",
    ],
    keywords: [
      "property",
      "building",
      "house",
      "home",
      "household",
      "office",
      "shop",
      "warehouse",
      "factory",
      "contents",
      "stock",
      "rent",
      "fire",
      "flood",
      "storm",
      "earthquake",
      "burglary",
      "theft",
      "industrial all risk",
      "premises",
      "asset",
    ],
  },
  {
    id: "travel",
    title: "Travel Insurance",
    category: "Personal",
    route: "/products/travel-insurance",
    summary:
      "Cover for holiday and business travelers against medical, financial, and property losses while outside their country of residence.",
    covers: [
      "Medical emergencies, accidents, and sickness while traveling",
      "Lost baggage and property loss",
      "Trip protection and trip cancellation support",
      "Holiday and business travel packages",
    ],
    bestFor: [
      "holiday travelers",
      "business travelers",
      "students",
      "families traveling abroad",
    ],
    keywords: [
      "travel",
      "trip",
      "holiday",
      "vacation",
      "business trip",
      "visa",
      "flight",
      "baggage",
      "luggage",
      "medical abroad",
      "lost baggage",
      "cancellation",
      "outside country",
      "journey",
    ],
  },
  {
    id: "engineering",
    title: "Engineering Insurance",
    category: "Business",
    route: "/products/engineering-insurance",
    summary:
      "Specialist protection for projects, construction works, contractor machinery, equipment, machinery erection, breakdown, and electronic equipment.",
    covers: [
      "Plant All Risk for contractor machinery against environmental hazards",
      "Contractors All Risks for construction projects",
      "Erection All Risks for machinery erection",
      "Machinery Breakdown for equipment damage",
      "Electronic Equipment Policy for computers and data protection",
    ],
    bestFor: [
      "contractors",
      "construction companies",
      "project owners",
      "equipment operators",
    ],
    keywords: [
      "engineering",
      "construction",
      "contractor",
      "project",
      "plant",
      "machinery",
      "equipment",
      "erection",
      "breakdown",
      "computer",
      "data",
      "electronic",
      "car policy",
      "ear policy",
      "par",
      "contractors all risks",
      "machinery breakdown",
    ],
  },
  {
    id: "liability",
    title: "Liability Insurance",
    category: "Business",
    route: "/products/liability-insurance",
    summary:
      "Cover for businesses and professionals when third parties suffer injury, property damage, defective product loss, or professional negligence.",
    covers: [
      "Public Liability for injury or property damage to the public, including legal costs and employee vicarious liability",
      "Product Liability for damages caused by defective products, including exports",
      "Professional Indemnity for professionals such as engineers and doctors against breach-of-duty liabilities",
    ],
    bestFor: [
      "businesses",
      "professionals",
      "product sellers",
      "service providers",
    ],
    keywords: [
      "liability",
      "public liability",
      "product liability",
      "professional indemnity",
      "indemnity",
      "legal",
      "lawsuit",
      "third party",
      "injury",
      "damage to public",
      "defective product",
      "negligence",
      "breach of duty",
      "doctor",
      "engineer",
      "export",
    ],
  },
  {
    id: "pecuniary",
    title: "Pecuniary Insurance",
    category: "Business",
    route: "/products/pecuniary-insurance",
    summary:
      "Protection against direct financial loss, employee dishonesty, misappropriation, and business interruption after covered premises damage.",
    covers: [
      "Fidelity Guarantee for employee dishonesty, embezzlement, or misappropriation of funds and property",
      "Consequential Loss for business interruption, loss of profit, and wages after operational premises damage",
    ],
    bestFor: [
      "business owners",
      "finance teams",
      "employers",
      "companies with cash handling",
    ],
    keywords: [
      "pecuniary",
      "financial loss",
      "fidelity",
      "fidelity guarantee",
      "employee dishonesty",
      "dishonesty",
      "embezzlement",
      "misappropriation",
      "fraud",
      "business interruption",
      "loss of profit",
      "wages",
      "consequential loss",
      "funds",
    ],
  },
  {
    id: "specialty",
    title: "Specialty Lines Insurance",
    category: "Specialty",
    route: "/products/specialty-lines",
    summary:
      "Customized cover for specialized and complex risks, including cyber liability, marine insurance, oil and gas, offshore, and other high-risk operations.",
    covers: [
      "Cyber liability and other niche risks",
      "Marine insurance and cargo-related risks",
      "Oil and gas, offshore, and high-risk industry protection",
      "Customized cover where a standard policy may not fit",
    ],
    bestFor: [
      "marine operators",
      "oil and gas companies",
      "digital businesses",
      "specialized industries",
    ],
    keywords: [
      "specialty",
      "speciality",
      "specialized",
      "cyber",
      "marine",
      "cargo",
      "oil",
      "gas",
      "offshore",
      "high risk",
      "custom",
      "unique risk",
      "niche",
    ],
  },
];

const DEFAULT_SUGGESTIONS = [
  "Which insurance do I need?",
  "How do I report a claim?",
  "Show me motor cover",
  "Contact a broker",
];

const createMessageId = () =>
  `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const normalize = (text) =>
  text
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const includesAny = (text, keywords) =>
  keywords.some((keyword) => text.includes(keyword));

const asBullets = (items) => items.map((item) => `- ${item}`).join("\n");

const actions = {
  products: [{ label: "View all products", href: "/our-products" }],
  contact: [
    { label: "Contact form", href: "/#contact" },
    { label: "WhatsApp", href: CONTACT_DETAILS.whatsapp, external: true },
  ],
  claim: [{ label: "Report a claim", href: "/report-a-claim" }],
};

const defaultMessages = [
  {
    id: "welcome-message",
    from: "bot",
    text: "Hello, I am Thrive Assist. I can help you understand Thrive Insurance Brokers' products, prepare a claim, find contact details, or choose a good starting cover based on what you want to protect.",
    suggestions: DEFAULT_SUGGESTIONS,
  },
];

const scoreProducts = (normalizedInput) =>
  PRODUCTS.map((product) => {
    const title = normalize(product.title);
    let score = normalizedInput.includes(product.id) ? 4 : 0;

    if (normalizedInput.includes(title)) {
      score += 6;
    }

    product.keywords.forEach((keyword) => {
      if (normalizedInput.includes(keyword)) {
        score += keyword.includes(" ") ? 4 : 2;
      }
    });

    product.bestFor.forEach((audience) => {
      if (normalizedInput.includes(normalize(audience))) {
        score += 2;
      }
    });

    return { product, score };
  }).sort((a, b) => b.score - a.score);

const productActions = (product) => [
  { label: "View product", href: product.route },
  ...actions.contact,
];

const productSuggestions = (product) => [
  `How do I get a quote for ${product.title}?`,
  "How do I report a claim?",
  "Show all products",
];

const createProductReply = (product, context, options = {}) => {
  const lead = options.recommended
    ? `Based on what you described, ${product.title} is the strongest starting point.`
    : `${product.title} is one of Thrive's ${product.category.toLowerCase()} insurance solutions.`;

  return {
    message: {
      from: "bot",
      text: `${lead}\n\n${product.summary}\n\nWhat it can include:\n${asBullets(
        product.covers,
      )}\n\nBest next step: review the product page, then contact Thrive so a broker can confirm the exact scope, insurer requirements, limits, and documents needed.`,
      actions: productActions(product),
      suggestions: productSuggestions(product),
    },
    context: {
      ...context,
      lastIntent: "product",
      lastProduct: product.id,
    },
  };
};

const createComparisonReply = (matches, context) => {
  const selected = matches.slice(0, 3).map(({ product }) => product);

  return {
    message: {
      from: "bot",
      text: `Here is a quick comparison based on your question:\n\n${selected
        .map((product) => `${product.title}: ${product.summary}`)
        .join(
          "\n\n",
        )}\n\nIf you tell me what you want to protect, I can narrow this to one or two likely starting points.`,
      actions: actions.products,
      suggestions: selected.map(
        (product) => `Tell me more about ${product.title}`,
      ),
    },
    context: {
      ...context,
      lastIntent: "comparison",
      lastProduct: selected[0]?.id || context.lastProduct,
    },
  };
};

const createProductListReply = (context) => ({
  message: {
    from: "bot",
    text: `Thrive's product categories cover personal, business, and specialty risks.\n\n${asBullets(
      PRODUCTS.map((product) => `${product.title}: ${product.summary}`),
    )}\n\nA simple way to choose: tell me the asset, person, trip, project, or business risk you want to protect.`,
    actions: actions.products,
    suggestions: [
      "I need cover for a vehicle",
      "I need cover for a building",
      "I need cover for business liability",
      "I need travel insurance",
    ],
  },
  context: {
    ...context,
    lastIntent: "products",
    lastProduct: null,
  },
});

const createClaimReply = (context, product) => ({
  message: {
    from: "bot",
    text: `To report a claim, use Thrive's Report a Claim form.\n\nYou will be asked for:\n${asBullets(
      [
        "Insured name",
        "Policy number",
        "Email address",
        "Phone number",
        "Optional supporting file upload",
      ],
    )}\n\nUseful supporting documents may include photos, receipts, accident reports, police reports, repair estimates, medical/travel documents, or other evidence of loss, depending on the policy. ${
      product
        ? `Since you mentioned ${product.title}, include any documents directly related to that policy.`
        : "If you are unsure what to attach, submit the form with the essentials and Thrive's team can guide you."
    }`,
    actions: [
      ...actions.claim,
      { label: "Call Desk 1", href: "tel:+2348180996418" },
      { label: "WhatsApp", href: CONTACT_DETAILS.whatsapp, external: true },
    ],
    suggestions: [
      "What documents do I need?",
      "Contact a broker",
      "Show all products",
    ],
  },
  context: {
    ...context,
    lastIntent: "claim",
    lastProduct: product?.id || context.lastProduct,
  },
});

const createQuoteReply = (context, product) => ({
  message: {
    from: "bot",
    text: `${
      product
        ? `For a ${product.title} quote, Thrive will need details about the risk you want to insure.`
        : "For a quote, Thrive will first need to know the product or risk you want covered."
    }\n\nHelpful details to prepare:\n${asBullets([
      "Your name, phone number, and email",
      "The type of insurance you need",
      "Asset, vehicle, trip, project, business, or professional details",
      "Current policy details, policy number, or prior cover if available",
      "Any preferred start date, coverage limit, or special concern",
    ])}\n\nPremiums depend on the selected cover, risk details, limits, and insurer terms, so a broker should confirm the final quote.`,
    actions: product ? productActions(product) : actions.contact,
    suggestions: [
      "Which insurance do I need?",
      "Contact a broker",
      "Show all products",
    ],
  },
  context: {
    ...context,
    lastIntent: "quote",
    lastProduct: product?.id || context.lastProduct,
  },
});

const createContactReply = (context) => ({
  message: {
    from: "bot",
    text: `You can reach Thrive Insurance Brokers Limited through these channels:\n\n${asBullets(
      [
        `Desk 1: ${CONTACT_DETAILS.deskOne}`,
        `Desk 2: ${CONTACT_DETAILS.deskTwo}`,
        `Email: ${CONTACT_DETAILS.email}`,
        `Office: ${CONTACT_DETAILS.address}`,
        "WhatsApp: available through the floating WhatsApp button",
      ],
    )}\n\nFor a detailed request, the contact form on the home page asks for your name, email, phone, and message.`,
    actions: [
      { label: "Contact form", href: "/#contact" },
      { label: "Email Thrive", href: `mailto:${CONTACT_DETAILS.email}` },
      { label: "WhatsApp", href: CONTACT_DETAILS.whatsapp, external: true },
    ],
    suggestions: [
      "How do I get a quote?",
      "How do I report a claim?",
      "Show all products",
    ],
  },
  context: {
    ...context,
    lastIntent: "contact",
  },
});

const createAboutReply = (context) => ({
  message: {
    from: "bot",
    text: `Thrive Insurance Brokers Limited is a registered insurance broker providing risk management and insurance solutions for businesses and individuals.\n\nThe company helps clients quantify and manage risk so they can survive and thrive. Its mission is to provide outstanding insurance intermediation through continuous customer evaluation and cutting-edge technology. Its vision is to be a thought leader in Africa's insurance industry while delivering superior service.\n\nCore values: Proactive, Reliable, Openness, Team Work, Exceptional Service, Customer Focus, and Technology.`,
    actions: [
      { label: "Who we are", href: "/who-we-are" },
      { label: "What we do", href: "/what-we-do" },
      ...actions.contact,
    ],
    suggestions: [
      "Are you licensed?",
      "What services do you offer?",
      "Contact a broker",
    ],
  },
  context: {
    ...context,
    lastIntent: "about",
  },
});

const createRegulationReply = (context) => ({
  message: {
    from: "bot",
    text: "Yes. Thrive Insurance Brokers Limited is registered and licensed by the Nigerian Council of Registered Insurance Brokers (NCRIB) and the National Insurance Commission (NAICOM), in compliance with the Insurance Act of 2003 and the NCRIB Act of 2003.",
    actions: [
      { label: "Who we are", href: "/who-we-are" },
      { label: "FAQs", href: "/faqs" },
    ],
    suggestions: [
      "What services do you offer?",
      "Contact a broker",
      "Show all products",
    ],
  },
  context: {
    ...context,
    lastIntent: "regulation",
  },
});

const createIndustryReply = (context) => ({
  message: {
    from: "bot",
    text: `Thrive serves businesses and individuals across several industries, including manufacturing, construction, healthcare, agriculture, hospitality, transportation, and related sectors.\n\nFor businesses, the most common starting points are Property, Liability, Engineering, Pecuniary, Specialty Lines, Motor, and Keyman Life cover. Tell me your industry and the main risk you are worried about, and I can point you toward a likely product.`,
    actions: actions.products,
    suggestions: [
      "I run a construction business",
      "I sell products to customers",
      "I need cover for my office",
      "I handle company funds",
    ],
  },
  context: {
    ...context,
    lastIntent: "industries",
  },
});

const createIndividualReply = (context) => ({
  message: {
    from: "bot",
    text: `Yes. Individuals can get insurance through Thrive.\n\nCommon personal starting points are:\n${asBullets(
      [
        "Life and Personal Insurance for term life, family protection, and related personal needs",
        "Motor Insurance for private vehicles and third-party road risks",
        "Travel Insurance for medical emergencies, baggage, and trip-related losses abroad",
        "Property Insurance where household, home, fixtures, valuables, and public liability protection is needed",
      ],
    )}\n\nIf you tell me what you want to protect, I can suggest the closest product page and next step.`,
    actions: actions.products,
    suggestions: [
      "I need life insurance",
      "I need motor insurance",
      "I need travel insurance",
      "I need household cover",
    ],
  },
  context: {
    ...context,
    lastIntent: "individual",
  },
});

const createPrivacyReply = (context) => ({
  message: {
    from: "bot",
    text: "Thrive's site policies explain how contact, newsletter, and claim information is handled. Contact requests collect name, email, phone, and message. Claim submissions collect insured name, policy number, email, phone, and supporting documents where provided.",
    actions: [
      { label: "Privacy Policy", href: "/privacy-policy" },
      { label: "Terms", href: "/terms-and-conditions" },
      { label: "Cookie Policy", href: "/cookie-policy" },
    ],
    suggestions: [
      "Contact a broker",
      "How do I report a claim?",
      "Show all products",
    ],
  },
  context: {
    ...context,
    lastIntent: "privacy",
  },
});

const createRecommendationReply = (context, matchedProduct) => {
  if (matchedProduct) {
    return createProductReply(matchedProduct, context, { recommended: true });
  }

  return {
    message: {
      from: "bot",
      text: `I can narrow it down quickly. Choose the closest thing you want to protect:\n\n${asBullets(
        [
          "Vehicle, fleet, goods in transit, or road risks: Motor Insurance",
          "Building, home, contents, stock, or business premises: Property Insurance",
          "Family protection, term life, or a key employee: Life and Personal Insurance",
          "Construction project, machinery, equipment, computers, or data: Engineering Insurance",
          "Customer injury, public damage, defective products, or professional duty: Liability Insurance",
          "Employee dishonesty, fraud, misappropriation, or loss of profit: Pecuniary Insurance",
          "Trips, medical emergencies abroad, baggage, or cancellations: Travel Insurance",
          "Cyber, marine, oil and gas, offshore, cargo, or unusual risks: Specialty Lines",
        ],
      )}\n\nTell me which line sounds closest and I will explain the next step.`,
      actions: actions.products,
      suggestions: [
        "I need cover for a vehicle",
        "I need cover for business liability",
        "I need cover for a construction project",
        "I need cyber or marine cover",
      ],
    },
    context: {
      ...context,
      lastIntent: "recommendation",
    },
  };
};

const createGreetingReply = (context) => ({
  message: {
    from: "bot",
    text: "Hi, I am here. Tell me what you want to protect or what you need to do on the site, and I will guide you to the right Thrive product, claim form, or contact channel.",
    actions: actions.products,
    suggestions: DEFAULT_SUGGESTIONS,
  },
  context: {
    ...context,
    lastIntent: "greeting",
  },
});

const createFallbackReply = (context) => ({
  message: {
    from: "bot",
    text: "I could not confidently match that to a specific Thrive service yet. Try describing the person, property, vehicle, trip, project, business activity, or loss you want help with. I can then suggest a likely cover, explain the claim path, or connect you with Thrive's team.",
    actions: [...actions.products, ...actions.contact],
    suggestions: DEFAULT_SUGGESTIONS,
  },
  context: {
    ...context,
    lastIntent: "fallback",
  },
});

const buildAssistantReply = (input, context) => {
  const normalizedInput = normalize(input);
  const productMatches = scoreProducts(normalizedInput);
  const topMatch = productMatches[0];
  const topProduct = topMatch?.score > 0 ? topMatch.product : null;
  const followUpToProduct =
    !topProduct &&
    context.lastProduct &&
    includesAny(normalizedInput, [
      "more",
      "details",
      "explain",
      "tell me more",
      "what about it",
      "that one",
      "this one",
      "it",
      "include",
      "coverage",
    ]);

  if (
    includesAny(normalizedInput, ["thank", "thanks", "appreciate"]) &&
    normalizedInput.length < 40
  ) {
    return {
      message: {
        from: "bot",
        text: "You are welcome. I can also help compare products, explain a claim, or connect you with Thrive's team.",
        suggestions: DEFAULT_SUGGESTIONS,
      },
      context: { ...context, lastIntent: "thanks" },
    };
  }

  if (
    includesAny(normalizedInput, [
      "claim",
      "claims",
      "report loss",
      "make a claim",
      "accident report",
      "loss report",
      "file a claim",
    ])
  ) {
    return createClaimReply(context, topProduct);
  }

  if (
    includesAny(normalizedInput, [
      "quote",
      "price",
      "cost",
      "premium",
      "how much",
      "estimate",
      "quotation",
      "buy",
      "purchase",
    ])
  ) {
    return createQuoteReply(context, topProduct);
  }

  if (
    includesAny(normalizedInput, [
      "contact",
      "phone",
      "call",
      "email",
      "address",
      "office",
      "location",
      "whatsapp",
      "reach",
      "support",
      "desk",
      "speak",
    ])
  ) {
    return createContactReply(context);
  }

  if (
    includesAny(normalizedInput, [
      "license",
      "licensed",
      "regulate",
      "regulated",
      "naicom",
      "ncrib",
      "insurance act",
    ])
  ) {
    return createRegulationReply(context);
  }

  if (
    context.lastIntent === "claim" &&
    includesAny(normalizedInput, [
      "document",
      "documents",
      "attach",
      "file",
      "upload",
      "evidence",
      "proof",
      "need",
      "required",
      "requirement",
      "requirements",
    ])
  ) {
    const product = context.lastProduct
      ? PRODUCTS.find((item) => item.id === context.lastProduct)
      : null;

    return createClaimReply(context, product);
  }

  if (
    includesAny(normalizedInput, [
      "privacy",
      "data",
      "cookie",
      "cookies",
      "terms",
      "personal information",
      "documents",
    ])
  ) {
    return createPrivacyReply(context);
  }

  if (
    includesAny(normalizedInput, [
      "who are you",
      "who is thrive",
      "about",
      "mission",
      "vision",
      "values",
      "core values",
      "what makes",
      "different",
      "broker",
    ])
  ) {
    return createAboutReply(context);
  }

  if (
    includesAny(normalizedInput, [
      "individual",
      "individuals",
      "personal insurance",
      "personal cover",
      "for myself",
      "for my family",
    ]) &&
    !topProduct
  ) {
    return createIndividualReply(context);
  }

  if (
    includesAny(normalizedInput, [
      "industry",
      "industries",
      "manufacturing",
      "construction",
      "healthcare",
      "agriculture",
      "hospitality",
      "transportation",
      "business",
      "company",
      "companies",
    ]) &&
    !topProduct
  ) {
    return createIndustryReply(context);
  }

  if (
    includesAny(normalizedInput, [
      "which",
      "recommend",
      "suggest",
      "need",
      "best",
      "right cover",
      "what cover",
      "what insurance",
      "help me choose",
      "protect",
    ])
  ) {
    return createRecommendationReply(context, topProduct);
  }

  const compareMatches = productMatches.filter((match) => match.score > 0);
  if (
    includesAny(normalizedInput, ["compare", "difference", "versus", "vs"]) &&
    compareMatches.length > 1
  ) {
    return createComparisonReply(compareMatches, context);
  }

  if (topProduct) {
    return createProductReply(topProduct, context);
  }

  if (followUpToProduct) {
    const product = PRODUCTS.find((item) => item.id === context.lastProduct);
    if (product) {
      return createProductReply(product, context);
    }
  }

  if (
    includesAny(normalizedInput, [
      "products",
      "services",
      "coverage",
      "cover",
      "policy",
      "policies",
      "what do you offer",
      "offer",
    ])
  ) {
    return createProductListReply(context);
  }

  if (includesAny(normalizedInput, ["faq", "question", "questions"])) {
    return {
      message: {
        from: "bot",
        text: "The FAQs page answers common questions about Thrive, licensing, services, business support, industries served, and personal insurance options.",
        actions: [{ label: "Open FAQs", href: "/faqs" }],
        suggestions: [
          "Are you licensed?",
          "What services do you offer?",
          "Can individuals get insurance?",
        ],
      },
      context: { ...context, lastIntent: "faq" },
    };
  }

  if (includesAny(normalizedInput, ["404", "not found", "broken page"])) {
    return {
      message: {
        from: "bot",
        text: "If a page is not found, use the main navigation to return to Home, Our Products, Contact Us, FAQs, or Report a Claim. I can also link you directly to the area you need.",
        actions: [
          { label: "Home", href: "/" },
          ...actions.products,
          ...actions.claim,
        ],
        suggestions: DEFAULT_SUGGESTIONS,
      },
      context: { ...context, lastIntent: "navigation" },
    };
  }

  if (
    includesAny(normalizedInput, [
      "hello",
      "hi",
      "hey",
      "good morning",
      "good afternoon",
      "good evening",
    ]) &&
    normalizedInput.split(" ").length <= 4
  ) {
    return createGreetingReply(context);
  }

  return createFallbackReply(context);
};

const Chatbot = ({ cookieAccepted = true }) => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(defaultMessages);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [chatContext, setChatContext] = useState({
    lastIntent: "welcome",
    lastProduct: null,
  });
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);

  const activeSuggestions = useMemo(() => {
    const latestBotWithSuggestions = [...messages]
      .reverse()
      .find((message) => message.from === "bot" && message.suggestions?.length);

    return latestBotWithSuggestions?.suggestions || DEFAULT_SUGGESTIONS;
  }, [messages]);

  useEffect(() => {
    if (!open) return;

    const handleEsc = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    window.addEventListener("keydown", handleEsc);
    window.setTimeout(() => inputRef.current?.focus(), 120);

    return () => window.removeEventListener("keydown", handleEsc);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping, open]);

  const sendMessage = (messageText = input) => {
    const trimmedInput = messageText.trim();
    if (!trimmedInput || isTyping) return;

    const userMessage = {
      id: createMessageId(),
      from: "user",
      text: trimmedInput,
    };
    const reply = buildAssistantReply(trimmedInput, chatContext);
    const replyDelay = Math.min(1300, 600 + trimmedInput.length * 8);

    setMessages((currentMessages) => [...currentMessages, userMessage]);
    setInput("");
    setIsTyping(true);

    window.setTimeout(() => {
      setMessages((currentMessages) => [
        ...currentMessages,
        {
          id: createMessageId(),
          ...reply.message,
        },
      ]);
      setChatContext(reply.context);
      setIsTyping(false);
    }, replyDelay);
  };

  const handleClear = () => {
    setMessages(defaultMessages);
    setChatContext({ lastIntent: "welcome", lastProduct: null });
    setInput("");
    setIsTyping(false);
    window.setTimeout(() => inputRef.current?.focus(), 80);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    sendMessage();
  };

  return (
    <div
      className={`chatbot-container ${!cookieAccepted ? "hidden-on-mobile" : ""}`}
    >
      {open ? (
        <section
          className="chatbot-window"
          role="dialog"
          aria-modal="false"
          aria-label="Thrive Insurance assistant"
        >
          <header className="chatbot-header">
            <div className="chatbot-title-group">
              <span className="chatbot-avatar" aria-hidden="true">
                <FaRobot />
              </span>
              <div>
                <span className="chatbot-title">Thrive Assist</span>
                <span className="chatbot-status">
                  Site-aware insurance guide
                </span>
              </div>
            </div>
            <button
              type="button"
              className="chatbot-close"
              onClick={() => setOpen(false)}
              aria-label="Close chat"
            >
              <FaTimes />
            </button>
          </header>

          <div className="chatbot-messages" aria-live="polite">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`chatbot-message-row ${message.from}`}
              >
                <div className={`chatbot-message ${message.from}`}>
                  {message.from === "bot" && (
                    <span className="chatbot-message-name">Thrive Assist</span>
                  )}
                  <span className="chatbot-message-text">{message.text}</span>
                  {message.actions?.length > 0 && (
                    <div className="chatbot-actions">
                      {message.actions.map((action) => (
                        <a
                          key={`${message.id}-${action.label}`}
                          href={action.href}
                          className="chatbot-action"
                          target={action.external ? "_blank" : undefined}
                          rel={
                            action.external ? "noopener noreferrer" : undefined
                          }
                        >
                          {action.label}
                          {action.external && (
                            <FaExternalLinkAlt aria-hidden="true" />
                          )}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {isTyping && (
              <div className="chatbot-message-row bot">
                <div
                  className="chatbot-message bot chatbot-typing"
                  aria-label="Thrive Assist is typing"
                >
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="chatbot-suggestions" aria-label="Suggested questions">
            {activeSuggestions.slice(0, 4).map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                onClick={() => sendMessage(suggestion)}
                disabled={isTyping}
              >
                {suggestion}
              </button>
            ))}
          </div>

          <form className="chatbot-input" onSubmit={handleSubmit}>
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="Ask about products, claims, quotes..."
              aria-label="Type your message"
              disabled={isTyping}
            />
            <button
              type="button"
              className="chatbot-reset"
              onClick={handleClear}
              aria-label="Clear chat"
            >
              <FaRedo />
            </button>
            <button
              type="submit"
              className="chatbot-send"
              disabled={!input.trim() || isTyping}
              aria-label="Send message"
            >
              <FaPaperPlane />
            </button>
          </form>
        </section>
      ) : (
        <button
          type="button"
          className="chatbot-fab"
          onClick={() => setOpen(true)}
          aria-label="Open Thrive Assist chat"
        >
          <FaComments size={22} aria-hidden="true" />
          <span>Chat</span>
        </button>
      )}
    </div>
  );
};

export default Chatbot;
